/**
 * @file Service für fortsetzbare ("resumable") Datei-Uploads.
 *
 * Ziele:
 * - Schnelle Rückgabe einer Download-URL, bevor der Upload abgeschlossen ist.
 * - Upload im Hintergrund, auch wenn die UI geschlossen wird.
 * - Fortschrittsverfolgung über Callbacks.
 * - Automatisches Fortsetzen von unterbrochenen Uploads.
 * - Priorisierung von Chunks, die für "Live-Downloads" benötigt werden.
 * - Parallele Upload-Streams, um Lücken zu füllen.
 */

import { API_BASE_URL, request } from './api.js'

const streamFailureCount = new Map()
function recordStreamFailure(fileRef) {
  const count = streamFailureCount.get(fileRef) || 0
  streamFailureCount.set(fileRef, count + 1)
}
function shouldDisableStreaming(fileRef) {
  return (streamFailureCount.get(fileRef) || 0) >= 2
}
const DEFAULT_CHUNK_SIZE = 1024 * 1024
const DEFAULT_POLL_INTERVAL = 1200
const activeJobsByKey = new Map()
const activeJobsByRef = new Map()

/**
 * Erstellt eine temporäre, lokale URL für eine Datei.
 * @param {File} file
 * @returns {{url: string, revoke: function}}
 */
export function createLocalDownloadLink(file) {
  const url = URL.createObjectURL(file)
  return { url, revoke: () => URL.revokeObjectURL(url) }
}

/**
 * Initialisiert einen mehrteiligen Upload beim Backend.
 * @param {object} params - Parameter wie filename, contentType, totalSize.
 */
export async function initPartialUpload({ filename, contentType, totalSize }) {
  return request('/users/files/parts/init', {
    method: 'POST',
    body: JSON.stringify({
      filename,
      content_type: contentType || null,
      total_size: totalSize ?? null
    })
  })
}

/**
 * Fragt den aktuellen Upload-Status (inkl. fehlender Teile) vom Backend ab.
 * @param {string} fileRef - Die Referenz zur Datei.
 */
export async function getPartialUploadStatus(fileRef) {
  return request(`/users/files/parts/${encodeURIComponent(fileRef)}/status`)
}

/**
 * Schließt einen mehrteiligen Upload serverseitig ab.
 * @param {string} fileRef - Die Referenz zur Datei.
 */
export async function closePartialUpload(fileRef) {
  return request(`/users/files/parts/${encodeURIComponent(fileRef)}/close`, { method: 'POST' })
}

function supportsRequestStreams(fileRef) {
  try {
    if (fileRef && shouldDisableStreaming(fileRef)) return false

    const rs = new ReadableStream({
      start(c) {
        c.close()
      }
    })

    const req = new Request('about:blank', {
      method: 'POST',
      body: rs,
      duplex: 'half'
    })

    return req.duplex === 'half'
  } catch {
    return false
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function fileKey(file) {
  return [file?.name || '', file?.size || 0, file?.lastModified || 0, file?.type || ''].join('|')
}

function normalizeMissing(missing) {
  if (!missing) return []
  if (Array.isArray(missing)) return missing.filter(Boolean)
  if (typeof missing === 'object') return [missing]
  return []
}

function mergeRanges(ranges) {
  const items = (ranges || [])
    .map((r) => [Math.max(0, Number(r?.start ?? r?.[0] ?? 0)), Math.max(0, Number(r?.end ?? r?.[1] ?? -1))])
    .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e) && e >= s)
    .sort((a, b) => a[0] - b[0])

  const merged = []
  for (const [start, end] of items) {
    const last = merged[merged.length - 1]
    if (!last || start > last[1] + 1) merged.push([start, end])
    else last[1] = Math.max(last[1], end)
  }
  return merged
}

/**
 * Berechnet das Ende des zusammenhängenden, bereits hochgeladenen Bereichs.
 * @param {Array<Array<number>>} ranges - Liste der empfangenen Bereiche.
 * @returns {number}
 */
function contiguousEnd(ranges) {
  const merged = mergeRanges(ranges)
  if (!merged.length || merged[0][0] > 0) return -1
  let end = merged[0][1]
  for (let i = 1; i < merged.length; i++) {
    const [start, nextEnd] = merged[i]
    if (start > end + 1) break
    end = Math.max(end, nextEnd)
  }
  return end
}

/**
 * Findet den Start der nächsten Lücke in den hochgeladenen Daten.
 * @param {Array<Array<number>>} ranges - Liste der empfangenen Bereiche.
 * @param {number} start - Die Position, ab der gesucht werden soll.
 * @returns {number}
 */
function nextMissingStart(ranges, start) {
  const merged = mergeRanges(ranges)
  let pos = Math.max(0, Number(start) || 0)
  for (const [s, e] of merged) {
    if (pos < s) return pos
    if (pos <= e) pos = e + 1
  }
  return pos
}

/**
 * Findet den niedrigsten Cursor-Wert eines anderen parallelen Upload-Segments.
 * @param {object} job - Das Haupt-Upload-Job-Objekt.
 * @param {string} selfKey - Der Schlüssel des aktuellen Segments.
 * @param {number} selfCursor - Der Cursor des aktuellen Segments.
 * @returns {number|null}
 */
function nextForeignCursor(job, selfKey, selfCursor) {
  let min = null
  for (const seg of job.segments.values()) {
    if (seg.key === selfKey) continue
    const c = Number(seg.cursor ?? seg.start ?? 0)
    if (!Number.isFinite(c) || c <= selfCursor) continue
    if (min === null || c < min) min = c
  }
  return min
}

/**
 * Prüft, ob ein anderer paralleler Upload-Segment bereits an einer bestimmten Position arbeitet.
 * @param {object} job - Das Haupt-Upload-Job-Objekt.
 * @param {number} cursor - Die zu prüfende Position.
 * @param {string|null} excludeKey - Der Schlüssel des eigenen Segments, das ignoriert werden soll.
 */
function hasActiveCursor(job, cursor, excludeKey = null) {
  for (const seg of job.segments.values()) {
    if (seg.key === excludeKey) continue
    if (Number(seg.cursor ?? seg.start ?? -1) === Number(cursor)) return true
  }
  return false
}

/**
 * Erstellt einen `ReadableStream` für einen Teil einer Datei, der hochgeladen werden soll.
 * @param {File} file - Die Datei.
 * @param {number} start - Start-Byte.
 * @param {number} end - End-Byte.
 * @param {number} chunkSize - Die Größe der Chunks.
 * @param {AbortSignal} signal - Signal zum Abbrechen.
 * @param {function} onChunk - Callback, der nach jedem Chunk aufgerufen wird.
 * @param {function} getUpperLimit - Funktion, um die obere Grenze dynamisch zu bestimmen.
 */
function createRangeBody(file, start, end, chunkSize, signal, onChunk, getUpperLimit, fileRef) {
  if (!supportsRequestStreams(fileRef)) return null

  let offset = start
  const hardUpper = Math.min(end, file.size - 1)

  return new ReadableStream({
    async start(controller) {
      try {
        while (offset <= hardUpper) {
          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

          const limit = Math.min(
            hardUpper,
            Math.max(
              offset,
              typeof getUpperLimit === 'function'
                ? Number(getUpperLimit(offset))
                : hardUpper
            )
          )

          if (offset > limit) break

          const next = Math.min(offset + chunkSize, limit + 1)
          const slice = file.slice(offset, next)
          const buffer = await slice.arrayBuffer()

          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

          controller.enqueue(new Uint8Array(buffer))
          offset = next

          if (typeof onChunk === 'function') onChunk(offset - 1, buffer.byteLength)
        }

        controller.close()
      } catch (err) {
        try {
          controller.error(err)
        } catch (e) {
          console.log(e)
        }
      }
    },
    cancel() {}
  })
}

/**
 * Synchronisiert den lokalen Job-Status mit den vom Server empfangenen Daten.
 * @param {object} job - Das Job-Objekt.
 * @param {object} status - Die Status-Antwort vom Server.
 */
function syncStatusRanges(job, status) {
  const record = status?.answer?.record || status?.record || null
  if (record?.received_ranges) job.ackedRanges = mergeRanges(record.received_ranges)
  if (Number.isFinite(Number(record?.size_bytes))) job.fileSizeOnServer = Number(record.size_bytes)
  job.contiguous = contiguousEnd(job.ackedRanges)
  return record
}

/**
 * Lädt einen bestimmten Byte-Bereich einer Datei zum Server hoch.
 * @param {object} job - Das Haupt-Upload-Job-Objekt.
 * @param {number} start - Start-Byte.
 * @param {number} end - End-Byte.
 * @param {string} [kind='main'] - Die Art des Uploads ('main', 'live', 'resume').
 */
async function uploadRangeToServer(job, start, end, kind = 'main') {
  // Holt die Job-Parameter für einfacheren Zugriff.
  const file = job.file
  const fileRef = job.fileRef
  const chunkSize = job.chunkSize
  // Erstellt einen AbortController, um diesen spezifischen Upload-Stream bei Bedarf abbrechen zu können.
  const controller = new AbortController()
  const targetEnd = end ?? (file.size - 1)
  // Erzeugt einen eindeutigen Schlüssel für dieses Upload-Segment, um Duplikate zu vermeiden.
  const key = `${start}:${targetEnd}:${kind}`

  // Wenn bereits ein identisches Segment hochgeladen wird, kehre zurück.
  if (job.segments.has(key)) return job.segments.get(key).promise
  // Wenn bereits ein anderer Upload an der gleichen Startposition arbeitet, warte.
  if (hasActiveCursor(job, start, key)) return Promise.resolve(null)

  // Erstellt ein Objekt, das den Zustand dieses Upload-Segments verfolgt.
  const segment = {
    key,
    start,
    cursor: start,
    end: targetEnd,
    kind,
    controller,
    running: true,
    promise: null
  }
  job.segments.set(key, segment)

  const promise = (async () => {
    try {
      // Die Schleife läuft, solange der Job nicht abgebrochen wurde und es noch etwas zu senden gibt.
      while (!job.cancelled) {
        // Finde den nächsten Startpunkt, basierend auf den bereits vom Server bestätigten Bereichen.
        segment.cursor = nextMissingStart(job.ackedRanges, segment.cursor)
        // Wenn der Cursor das Ende dieses Segments erreicht hat, ist die Arbeit hier getan.
        if (segment.cursor > targetEnd) break

        // Finde die Startposition des nächsten parallelen Uploads, um nicht damit in Konflikt zu geraten.
        const foreign = nextForeignCursor(job, key, segment.cursor)
        // Die Obergrenze für diesen Upload-Durchlauf ist entweder das Ende des Segments oder der Start des nächsten parallelen Uploads.
        const limit = foreign === null ? targetEnd : Math.min(targetEnd, foreign - 1)
        // Wenn unser Cursor bereits über diesem Limit liegt, warten wir auf den nächsten Durchlauf.
        if (segment.cursor > limit) break

        // Baut die URL für den Stream-Upload zusammen.
        const url = new URL(`${API_BASE_URL}/users/files/parts/${encodeURIComponent(fileRef)}/stream`)
        url.searchParams.set('start', String(segment.cursor))
        url.searchParams.set('total_size', String(file.size))

        // Erstellt den Request-Body. Wenn Streaming unterstützt wird, wird ein ReadableStream verwendet,
        // ansonsten ein einfacher Blob (Slice der Datei).
        const body = createRangeBody(
          file,
          segment.cursor,
          limit,
          chunkSize,
          controller.signal,
          (lastByte, chunkBytes) => {
            segment.cursor = Math.max(segment.cursor, lastByte + 1)
            if (typeof job.onProgress === 'function') {
              job.onProgress({
                fileRef,
                start: segment.start,
                current: lastByte,
                sent: Math.max(0, lastByte - segment.start + 1),
                total: file.size,
                percent: Math.min(100, Math.round(((lastByte + 1) / file.size) * 100)),
                kind,
                bytesInChunk: chunkBytes
              })
            }
          },
          () => {
            const nextForeign = nextForeignCursor(job, key, segment.cursor)
            return nextForeign === null ? targetEnd : Math.min(targetEnd, nextForeign - 1)
          },
          fileRef
        )

        // Führt den eigentlichen Fetch-Request aus.
        const response = await fetch(url.toString(), {
          method: 'POST',
          credentials: 'include',
          headers: file.type ? { 'Content-Type': file.type } : {},
          body: body || file.slice(segment.cursor, limit + 1),
          ...(body ? { duplex: 'half' } : {}),
          signal: controller.signal
        })

        if (!response.ok) throw new Error(`Upload fehlgeschlagen (${response.status})`)

        // Verarbeitet die Antwort vom Server, um den lokalen Status zu aktualisieren.
        const data = await response.json().catch(() => null)
        const record = data?.answer?.record || data?.answer || null
        if (record?.received_ranges) job.ackedRanges = mergeRanges(record.received_ranges)
        else job.ackedRanges = mergeRanges([...job.ackedRanges, [segment.start, Math.max(segment.start, segment.cursor - 1)]])
        // Berechnet das neue Ende des zusammenhängenden Bereichs.
        job.contiguous = contiguousEnd(job.ackedRanges)
        // Setzt den Cursor auf den nächsten fehlenden Startpunkt.
        segment.cursor = nextMissingStart(job.ackedRanges, segment.cursor)
        // Wenn der Cursor das Ende erreicht hat, beende die Schleife.
        if (segment.cursor > targetEnd) break
      }
    } catch (err) {
      // Fehlerbehandlung: Wenn der Job nicht absichtlich abgebrochen wurde,
      // versuche den Status zu aktualisieren und warte kurz, bevor es weitergeht.
      if (!job.cancelled && !controller.signal.aborted) {
        recordStreamFailure(fileRef)
        const status = await refreshServerNeed(fileRef).catch(() => null)
        syncStatusRanges(job, status)
        segment.cursor = nextMissingStart(job.ackedRanges, segment.cursor)
        if (segment.cursor <= targetEnd) await sleep(400)
      }
    } finally {
      // Aufräumen: Bricht den Controller ab, um alle laufenden Streams zu stoppen.
      try {
        controller.abort()
      } catch (e) {
        console.log(e)
      }
    }
  })()

  // Speichert das Promise und entfernt das Segment aus der Liste der aktiven Segmente, wenn es fertig ist.
  segment.promise = promise.finally(() => {
    segment.running = false
    job.segments.delete(key)
  })

  return segment.promise
}

/**
 * Wrapper, um den Server-Status abzufragen.
 * @param {string} fileRef
 */
export async function refreshServerNeed(fileRef) {
  return getPartialUploadStatus(fileRef)
}

/**
 * Stellt sicher, dass für alle "live" benötigten Lücken ein Upload-Prozess gestartet wird.
 * @param {object} job
 * @param {Array<object>} missingList - Liste der fehlenden Bereiche vom Server.
 */
async function ensureLiveUploads(job, missingList) {
  for (const miss of missingList) {
    if (!miss?.live) continue
    let start = Math.max(0, Number(miss.start ?? 0))
    const end = miss.end == null ? job.file.size - 1 : Math.max(start, Number(miss.end))
    start = nextMissingStart(job.ackedRanges, start)
    if (!Number.isFinite(start) || start > end) continue
    if (hasActiveCursor(job, start)) continue
    void uploadRangeToServer(job, start, end, 'live')
  }
}

/**
 * Bricht überholte Upload-Segmente ab.
 * @param {object} job
 */
function pruneOvertakenSegments(job) {
  job.contiguous = contiguousEnd(job.ackedRanges)
}

/**
 * Prüft, ob ein Upload basierend auf den Server-Daten als abgeschlossen betrachtet werden kann.
 * @param {object} job
 * @param {object} record - Der Metadaten-Record vom Server.
 */
function isJobComplete(job, record) {
  const total = Number(record?.total_size_bytes ?? job.file.size ?? 0)
  if (!Number.isFinite(total) || total <= 0) return false
  return contiguousEnd(job.ackedRanges) >= total - 1
}

/**
 * Die Haupt-Upload-Verwaltungsschleife.
 * @param {object} job
 */
async function runUploadJob(job) {
  let pollStopped = false
  try {
    // 1. Initialisiere den Upload beim Backend, um eine fileRef zu erhalten.
    const init = await initPartialUpload({
      filename: job.file.name,
      contentType: job.file.type,
      totalSize: job.file.size
    })

    // Extrahiere die fileRef aus der Server-Antwort.
    const ref =
      init?.answer?.file_ref ||
      init?.answer?.record?.id ||
      init?.answer?.record?.file_ref ||
      init?.answer?.fileRef

    if (!ref) throw new Error('Keine Datei-ID vom Server erhalten')
    job.fileRef = ref
    // Registriere den Job unter seiner fileRef für globalen Zugriff.
    activeJobsByRef.set(ref, job)
    // Synchronisiere den initialen Status.
    syncStatusRanges(job, init)

    if (typeof job.onReady === 'function') {
      // Benachrichtige den Aufrufer, dass der Upload bereit ist und eine URL hat.
      job.onReady({
        fileRef: ref,
        downloadUrl: job.downloadUrl,
        record: init?.answer?.record || null
      })
    }

    // 2. Starte einen Hintergrund-Poller, der regelmäßig den Server-Status abfragt.
    const poller = (async () => {
      while (!job.cancelled && !pollStopped) {
        try {
          const status = await refreshServerNeed(ref)
          const record = syncStatusRanges(job, status)
          const missing = normalizeMissing(status?.answer?.missing ?? status?.missing)

          // Wenn der Server "live" benötigte Teile meldet, starte dafür gezielte Uploads.
          if (missing.length) {
            await ensureLiveUploads(job, missing)
            if (typeof job.onNeed === 'function') {
              job.onNeed({ fileRef: ref, missing, record: record || null })
            }
          }

          // Bereinige überholte Upload-Segmente.
          pruneOvertakenSegments(job)
          if (record?.is_closed) break
        } catch {
          if (job.cancelled) break
        }
        await sleep(job.pollIntervalMs)
      }
    })()

    job.poller = poller
    // 3. Starte den Haupt-Upload für die gesamte Datei.
    void uploadRangeToServer(job, 0, job.file.size - 1, 'main')

    // 4. Haupt-Management-Schleife: Überwacht den Fortschritt und reagiert.
    while (!job.cancelled) {
      pruneOvertakenSegments(job)

      // Frage den Server-Status erneut ab.
      const status = await refreshServerNeed(ref).catch(() => null)
      const record = syncStatusRanges(job, status)
      const missing = normalizeMissing(status?.answer?.missing ?? status?.missing)

      // Wenn der Server meldet, dass die Datei geschlossen ist, ist der Job fertig.
      if (record?.is_closed) break

      if (missing.length) {
        // Wenn es Lücken gibt, starte gezielte Uploads, um sie zu füllen.
        await ensureLiveUploads(job, missing)
        const first = missing[0]
        const start = Math.max(0, Number(first?.start ?? 0))
        const end = first?.end == null ? job.file.size - 1 : Math.max(start, Number(first.end))
        if (start > job.contiguous) {
          await uploadRangeToServer(job, start, end, first?.live ? 'live' : 'resume')
        } else {
          await sleep(250)
        }
        continue
      }

      // Wenn keine Lücken gemeldet werden und keine Upload-Segmente mehr laufen...
      if (job.segments.size === 0) {
        // ...und die Datei laut unserem lokalen Status komplett ist...
        if (isJobComplete(job, record)) {
          // ...versuche, den Upload serverseitig abzuschließen.
          const closeResult = await closePartialUpload(ref).catch(async () => null)
          if (closeResult) {
            const finalStatus = await refreshServerNeed(ref).catch(() => null)
            const finalRecord = syncStatusRanges(job, finalStatus)
            const finished = {
              fileRef: ref,
              downloadUrl: job.downloadUrl,
              record: finalRecord || finalStatus?.answer?.record || null,
              missing: normalizeMissing(finalStatus?.answer?.missing ?? finalStatus?.missing)
            }
            job.finished = true
            // Benachrichtige den Aufrufer und löse das Haupt-Promise auf.
            if (typeof job.onDone === 'function') job.onDone(finished)
            job.resolve(finished)
            break
          }
        }
        await sleep(300)
        continue
      }

      await sleep(300)
    }

    // 5. Finale Überprüfung und Abschluss, falls die Schleife beendet wurde.
    if (!job.finished) {
      const finalStatus = await refreshServerNeed(ref).catch(() => null)
      const finalRecord = syncStatusRanges(job, finalStatus)
      const finalMissing = normalizeMissing(finalStatus?.answer?.missing ?? finalStatus?.missing)

      if (!finalMissing.length && isJobComplete(job, finalRecord)) {
        const closeResult = await closePartialUpload(ref).catch(async () => null)
        if (closeResult) {
          const afterClose = await refreshServerNeed(ref).catch(() => null)
          const afterRecord = syncStatusRanges(job, afterClose)
          const finished = {
            fileRef: ref,
            downloadUrl: job.downloadUrl,
            record: afterRecord || afterClose?.answer?.record || null,
            missing: normalizeMissing(afterClose?.answer?.missing ?? afterClose?.missing)
          }
          job.finished = true
          if (typeof job.onDone === 'function') job.onDone(finished)
          job.resolve(finished)
          return
        }
      }
    }
  } catch (err) {
    if (typeof job.onError === 'function') job.onError(err)
    job.reject(err)
  } finally {
    // Aufräumen: Stoppe den Poller und entferne den Job aus den globalen Maps.
    pollStopped = true
    activeJobsByKey.delete(job.key)
    if (job.fileRef) activeJobsByRef.delete(job.fileRef)
  }
}

/**
 * Die öffentliche Funktion, um einen großen Upload zu starten.
 * @param {File} file
 * @param {object} [options={}] - Optionen und Callbacks.
 */
export function uploadHugeFile(file, options = {}) {
  const key = fileKey(file)
  const existing = activeJobsByKey.get(key)
  if (existing) return existing.publicApi

  const link = createLocalDownloadLink(file)
  let resolveDone
  let rejectDone

  const job = {
    key,
    file,
    fileRef: options.fileRef || null,
    downloadUrl: link.url,
    revoke: link.revoke,
    chunkSize: Math.max(64 * 1024, Number(options.chunkSize || DEFAULT_CHUNK_SIZE)),
    pollIntervalMs: Math.max(250, Number(options.pollIntervalMs || DEFAULT_POLL_INTERVAL)),
    onProgress: options.onProgress,
    onDone: options.onDone,
    onError: options.onError,
    onReady: options.onReady,
    onNeed: options.onNeed,
    ackedRanges: [],
    contiguous: -1,
    fileSizeOnServer: null,
    segments: new Map(),
    cancelled: false,
    finished: false,
    poller: null,
    resolve: (value) => resolveDone(value),
    reject: (err) => rejectDone(err),
    done: null,
    publicApi: null
  }

  job.done = new Promise((resolve, reject) => {
    resolveDone = resolve
    rejectDone = reject
  })

  job.cancel = async () => {
    job.cancelled = true
    for (const seg of job.segments.values()) {
      try {
        seg.controller.abort()
      } catch (e) {
        console.log(e)
      }
    }
    try {
      if (job.fileRef) await closePartialUpload(job.fileRef)
    } catch (e) {
      console.log(e)
    }
  }

  job.publicApi = {
    get downloadUrl() {
      return job.downloadUrl
    },
    get fileRef() {
      return job.fileRef
    },
    done: job.done,
    cancel: job.cancel,
    revoke: job.revoke,
    refreshStatus: () => (job.fileRef ? refreshServerNeed(job.fileRef) : Promise.resolve(null))
  }

  activeJobsByKey.set(key, job)
  void runUploadJob(job)
  return job.publicApi
}

/**
 * Alias für `uploadHugeFile`.
 */
export function startHugeFileUpload(file, options = {}) {
  return uploadHugeFile(file, options)
}

/**
 * Holt einen aktiven Upload-Job basierend auf dem File-Objekt.
 * @param {File} file
 */
export function getActiveUploadJob(file) {
  return activeJobsByKey.get(fileKey(file)) || null
}

/**
 * Holt einen aktiven Upload-Job basierend auf der fileRef.
 * @param {string} fileRef
 */
export function getActiveUploadJobByRef(fileRef) {
  return activeJobsByRef.get(String(fileRef || '')) || null
}